import { useState, useEffect, useCallback, useRef } from "react";
import { X, FileText, CheckCircle, Shield, Users, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { sendLeadToGrowthHub } from "@/lib/sendLeadToGrowthHub";
import { useSmartFormPersonalization } from "@/hooks/useSmartFormPersonalization";

interface ExitIntentPopupProps {
  onClose?: () => void;
}

const ExitIntentPopup = ({ onClose }: ExitIntentPopupProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastScrollY = useRef(0);
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
  const personalization = useSmartFormPersonalization(currentPath);
  const hasInteractedWithCalc = typeof sessionStorage !== "undefined" && sessionStorage.getItem("obc_calc_used") === "true";
  const lastScrollTime = useRef(Date.now());

  const wasShown = useCallback(() => {
    return sessionStorage.getItem("obc_exit_popup_shown") === "true";
  }, []);

  const markAsShown = useCallback(() => {
    sessionStorage.setItem("obc_exit_popup_shown", "true");
  }, []);

  const showPopup = useCallback(() => {
    if (!wasShown()) {
      setIsVisible(true);
      markAsShown();
    }
  }, [wasShown, markAsShown]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    markAsShown();
    onClose?.();
  }, [markAsShown, onClose]);

  // 1. Desktop mouse leave detection
  useEffect(() => {
    if (wasShown()) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) showPopup();
    };

    // Wait 30s before enabling exit-intent — let visitors engage first
    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 30000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [wasShown, showPopup]);

  // 2. Back button detection
  useEffect(() => {
    if (wasShown()) return;

    const handlePopState = () => {
      showPopup();
      window.history.pushState(null, "", window.location.href);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [wasShown, showPopup]);

  // 3. Idle timeout (45 seconds of no interaction)
  useEffect(() => {
    if (wasShown()) return;

    let idleTimer: ReturnType<typeof setTimeout>;

    const resetIdle = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        showPopup();
      }, 45000);
    };

    const events = ["scroll", "click", "touchstart", "mousemove", "keydown"];
    events.forEach((e) => document.addEventListener(e, resetIdle, { passive: true }));

    // Start the initial timer
    idleTimer = setTimeout(() => {
      showPopup();
    }, 45000);

    return () => {
      clearTimeout(idleTimer);
      events.forEach((e) => document.removeEventListener(e, resetIdle));
    };
  }, [wasShown, showPopup]);

  // 4. Tab visibility change (user returns to tab)
  useEffect(() => {
    if (wasShown()) return;

    let hasLeft = false;

    const handleVisibility = () => {
      if (document.hidden) {
        hasLeft = true;
      } else if (hasLeft) {
        showPopup();
      }
    };

    // Delay enabling so it doesn't fire on initial load
    const timer = setTimeout(() => {
      document.addEventListener("visibilitychange", handleVisibility);
    }, 10000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [wasShown, showPopup]);

  // 5. Rapid scroll-up on mobile
  useEffect(() => {
    if (wasShown()) return;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const currentTime = Date.now();
      const timeDelta = currentTime - lastScrollTime.current;
      const scrollDelta = lastScrollY.current - currentY; // positive = scrolling up

      if (timeDelta > 0 && timeDelta < 500 && scrollDelta > 200 && currentY < 300) {
        showPopup();
      }

      lastScrollY.current = currentY;
      lastScrollTime.current = currentTime;
    };

    // Only enable after 10 seconds on page
    const timer = setTimeout(() => {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }, 10000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [wasShown, showPopup]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      setError("Please enter your name.");
      return;
    }

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: insertError } = await supabase
        .from("crm_leads")
        .insert({
          name: trimmedName,
          email: email.trim(),
          phone: "",
          source_page: "exit_intent_guide",
          service_type: "lead-magnet",
          message: "Downloaded Florida Roofing Material Guide",
          marketing_consent: marketingConsent,
          lead_magnet: "florida_roofing_guide",
        });

      if (insertError) {
        console.error("Exit intent lead error:", insertError);
      }

      await sendLeadToGrowthHub({
        name: trimmedName,
        email: email.trim(),
        form_type: 'exit_intent',
        notes: 'Downloaded Florida Roofing Material Guide',
        service_interest: 'lead-magnet',
      });

      if (typeof window !== 'undefined' && (window as any).obcTracker?.trackEvent) {
        (window as any).obcTracker.trackEvent('form_submission', {
          name: trimmedName,
          email: email.trim(),
          form_type: 'exit_intent',
          service_interest: 'lead-magnet',
        });
      }

      setIsSubmitted(true);
      
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      <div className="relative bg-card rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {isSubmitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-accent" />
            </div>
            <h3 className="text-xl font-heading font-bold mb-2">
              Your Guide Is Ready!
            </h3>
            <p className="text-muted-foreground">
              Check your inbox — your PDF guide will arrive in the next few minutes.
            </p>
          </div>
        ) : (
          <>
            {/* Document-style header - personalized based on visitor intent */}
            <div className="bg-gradient-to-br from-primary to-primary/90 px-6 pt-8 pb-6 relative overflow-hidden">
              <div className="absolute -right-4 -bottom-2 w-28 h-36 bg-primary-foreground/10 rounded-sm rotate-6 shadow-lg" />
              <div className="absolute -right-2 -bottom-1 w-28 h-36 bg-primary-foreground/15 rounded-sm rotate-3 shadow-lg" />

              <div className="relative z-10 text-center">
                <div className="w-14 h-14 rounded-lg bg-accent/30 backdrop-blur flex items-center justify-center mx-auto mb-4">
                  {hasInteractedWithCalc ? <Phone className="h-7 w-7 text-accent" /> : <FileText className="h-7 w-7 text-accent" />}
                </div>
                {hasInteractedWithCalc ? (
                  <>
                    <p className="text-accent font-bold text-sm tracking-wide uppercase mb-2">
                      Wait — Your Estimate Is Almost Ready
                    </p>
                    <h3 className="text-xl font-heading font-bold text-primary-foreground mb-1">
                      {personalization.headline}
                    </h3>
                    <p className="text-primary-foreground/70 text-sm">
                      Get a personalized quote in under 60 seconds
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-accent font-bold text-sm tracking-wide uppercase mb-2">
                      Free Download — 2026 Edition
                    </p>
                    <h3 className="text-xl font-heading font-bold text-primary-foreground mb-1">
                      Save Thousands on Your Next Roof
                    </h3>
                    <p className="text-primary-foreground/70 text-sm">
                      The Florida Homeowner's Roofing Cost &amp; Insurance Guide
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="p-6">
              {/* Table of contents */}
              <div className="space-y-2.5 mb-4 text-sm">
                {[
                  { ch: "✓", title: "2026 Material Costs — Metal, Tile, Shingle & Flat" },
                  { ch: "✓", title: "Insurance Discounts You're Probably Missing" },
                  { ch: "✓", title: "When to Repair vs. Full Replacement" },
                  { ch: "✓", title: "Hurricane-Zone Code Requirements" },
                  { ch: "✓", title: "How to Save $2,000–$5,000 on Your Project" },
                ].map((item, i) => (
                  <div key={i} className="flex items-baseline gap-2">
                    <span className="text-xs font-bold text-accent w-4 flex-shrink-0">
                      {item.ch}
                    </span>
                    <span className="text-muted-foreground">{item.title}</span>
                  </div>
                ))}
              </div>

              {/* Metadata */}
              <p className="text-xs text-muted-foreground/70 mb-2">
                12 pages · PDF format · Updated March 2026
              </p>

              {/* Social proof */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-5">
                <Users className="h-3.5 w-3.5" />
                <span>4,200+ Florida homeowners downloaded this guide</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="exit-email" className="sr-only">Email</Label>
                  <Input
                    id="exit-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="exit-name" className="sr-only">Name</Label>
                  <Input
                    id="exit-name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSubmitting}
                    className="h-11"
                    required
                    minLength={2}
                    maxLength={80}
                    autoComplete="name"
                  />
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="marketing-consent"
                    checked={marketingConsent}
                    onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                  />
                  <Label 
                    htmlFor="marketing-consent" 
                    className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                  >
                    Yes, I'd like to receive roofing tips, special offers, and updates. 
                    We respect your privacy.
                  </Label>
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Get My Free Guide + Save Thousands"}
                </Button>
              </form>

              <button
                type="button"
                onClick={handleClose}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground mt-4 py-2 transition-colors"
              >
                No thanks, I don't need the guide
              </button>

              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
                <Shield className="h-3 w-3" />
                <span>Your info is secure & never shared</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExitIntentPopup;
